import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/ui/Alert';
import { Spinner } from '@/components/ui/Spinner';
import { formatMoney } from '@/lib/format';
import { useProfile } from '@/features/auth/useProfile';
import { AiPanel } from '@/features/ai/AiPanel';
import { Bar, StatTile } from './StatTile';
import {
  useAgentScorecard,
  useForecast,
  useFunnel,
  useLeakage,
  useMonthlyKpis,
  useTeamScorecard,
} from './useReports';

const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);
const num = (v: string | number | null | undefined) => Number(v ?? 0);

/**
 * The four role dashboards (#32–#35).
 *
 * They share one file because they share almost all their parts; what differs is
 * which numbers matter to whom, and that is a three-line decision each rather
 * than a separate screen each. None of them filters by team or owner: the views
 * are security_invoker, so RLS has already done it.
 */

// ------------------------------------------------------------------ CEO (#32)

export function CeoDashboard() {
  const { t } = useTranslation();
  const kpis = useMonthlyKpis();
  const teams = useTeamScorecard();
  const forecast = useForecast();

  if (kpis.isPending || teams.isPending) return <Spinner />;

  const months = kpis.data ?? [];
  const thisMonth = months[months.length - 1];
  const lastMonth = months[months.length - 2];
  const weighted = sum((forecast.data ?? []).map((f) => num(f.weighted_value)));

  const delta = (now?: number, before?: number) => {
    if (!before || before === 0 || now === undefined) return undefined;
    const pct = Math.round(((now - before) / before) * 100);
    return `${pct > 0 ? '+' : ''}${pct}%`;
  };

  return (
    <div className="flex flex-col gap-6">
      <section>
        <h2 className="mb-2 text-sm font-semibold text-slate-700">{t('dashboard.thisMonth')}</h2>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatTile
            label={t('dashboard.leads')}
            value={thisMonth?.leads ?? 0}
            hint={delta(thisMonth?.leads, lastMonth?.leads)}
          />
          <StatTile
            label={t('dashboard.sales')}
            value={thisMonth?.sales ?? 0}
            hint={delta(thisMonth?.sales, lastMonth?.sales)}
          />
          <StatTile label={t('dashboard.revenue')} value={formatMoney(thisMonth?.revenue)} />
          <StatTile
            label={t('dashboard.commission')}
            value={formatMoney(thisMonth?.commission)}
            tone="good"
          />
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold text-slate-700">{t('dashboard.forecast')}</h2>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatTile
            label={t('dashboard.weightedPipeline')}
            value={formatMoney(weighted)}
            hint={t('dashboard.weightedHint')}
          />
          <StatTile
            label={t('dashboard.marketingSpend')}
            value={formatMoney(thisMonth?.marketing_spend)}
          />
          <StatTile
            label={t('dashboard.cpl')}
            value={formatMoney(thisMonth?.cpl)}
            hint={t('dashboard.cplHint')}
          />
          <StatTile
            label={t('dashboard.roas')}
            value={thisMonth?.roas ? `${thisMonth.roas}×` : '—'}
            tone={num(thisMonth?.roas) >= 1 ? 'good' : 'warn'}
          />
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold text-slate-700">{t('dashboard.teams')}</h2>
        <TeamTable />
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold text-slate-700">{t('dashboard.twelveMonths')}</h2>
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                {(['month', 'leads', 'sales', 'revenue', 'commission', 'closeRate'] as const).map(
                  (k) => (
                    <th key={k} scope="col" className="px-3 py-2 text-start">
                      {t(`dashboard.cols.${k}` as 'dashboard.cols.month')}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {months.map((m) => (
                <tr key={m.month} className="hover:bg-slate-50">
                  <td className="whitespace-nowrap px-3 py-2 text-slate-700" dir="ltr">
                    {m.month.slice(0, 7)}
                  </td>
                  <td className="px-3 py-2 tabular-nums text-slate-700">{m.leads}</td>
                  <td className="px-3 py-2 tabular-nums text-slate-700">{m.sales}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-slate-700" dir="ltr">
                    {formatMoney(m.revenue)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-slate-700" dir="ltr">
                    {formatMoney(m.commission)}
                  </td>
                  <td className="px-3 py-2 tabular-nums text-slate-700">
                    {m.close_rate ? `${m.close_rate}%` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

// -------------------------------------------------------- sales manager (#33)

export function ManagerDashboard() {
  const { t } = useTranslation();
  const agents = useAgentScorecard();
  const leakage = useLeakage();
  const forecast = useForecast();

  if (agents.isPending) return <Spinner />;

  const rows = agents.data ?? [];
  const leaks = leakage.data ?? [];
  const weighted = sum((forecast.data ?? []).map((f) => num(f.weighted_value)));

  const neverContacted = sum(rows.map((r) => r.never_contacted));
  const overdue = sum(rows.map((r) => r.overdue));

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label={t('dashboard.leads')} value={sum(rows.map((r) => r.leads))} />
        <StatTile
          label={t('dashboard.neverContacted')}
          value={neverContacted}
          tone={neverContacted > 0 ? 'bad' : 'good'}
        />
        <StatTile
          label={t('dashboard.overdue')}
          value={overdue}
          tone={overdue > 0 ? 'warn' : 'good'}
        />
        <StatTile label={t('dashboard.weightedPipeline')} value={formatMoney(weighted)} />
      </div>

      {/* The leakage summary belongs on the manager's first screen, not three
          clicks away: #42 is the thing the client is buying. */}
      {leaks.length > 0 ? (
        <Alert tone="error">
          {t('dashboard.leakageAlert', { count: leaks.length })}{' '}
          <Link to="/reports?tab=leakage" className="font-semibold underline">
            {t('dashboard.viewLeakage')}
          </Link>
        </Alert>
      ) : null}

      {/* #37. Below the numbers on purpose: the dashboard above is fact, this is
          a summary of it, and a manager should read them in that order. */}
      <AiPanel feature="briefing" title={t('ai.briefingTitle')} />

      <section>
        <h2 className="mb-2 text-sm font-semibold text-slate-700">{t('dashboard.funnel')}</h2>
        <FunnelChart />
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold text-slate-700">{t('dashboard.agents')}</h2>
        <AgentTable rows={rows} />
      </section>
    </div>
  );
}

// ---------------------------------------------------------- team leader (#34)

export function TeamLeaderDashboard() {
  const { t } = useTranslation();
  const agents = useAgentScorecard();
  const leakage = useLeakage();

  if (agents.isPending) return <Spinner />;

  // No team filter here on purpose: RLS already limited the view to this
  // leader's team, and a second filter in the client would be a lie about where
  // the enforcement lives.
  const rows = agents.data ?? [];
  const leaks = leakage.data ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label={t('dashboard.teamLeads')} value={sum(rows.map((r) => r.leads))} />
        <StatTile label={t('dashboard.teamSales')} value={sum(rows.map((r) => r.sales))} />
        <StatTile
          label={t('dashboard.teamRevenue')}
          value={formatMoney(sum(rows.map((r) => num(r.revenue))))}
        />
        <StatTile
          label={t('dashboard.needsAttention')}
          value={leaks.length}
          tone={leaks.length > 0 ? 'warn' : 'good'}
        />
      </div>

      <section>
        <h2 className="mb-2 text-sm font-semibold text-slate-700">{t('dashboard.myTeam')}</h2>
        <AgentTable rows={rows} />
      </section>
    </div>
  );
}

// ---------------------------------------------------------------- sales (#35)

export function SalesDashboard() {
  const { t } = useTranslation();
  const { data: me } = useProfile();
  const agents = useAgentScorecard();

  if (agents.isPending) return <Spinner />;

  const mine = (agents.data ?? []).find((a) => a.agent_id === me?.id);

  if (!mine) {
    return <p className="text-sm text-slate-500">{t('dashboard.noDataYet')}</p>;
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label={t('dashboard.myLeads')} value={mine.leads} />
        <StatTile
          label={t('dashboard.neverContacted')}
          value={mine.never_contacted}
          tone={mine.never_contacted > 0 ? 'bad' : 'good'}
        />
        <StatTile
          label={t('dashboard.overdue')}
          value={mine.overdue}
          tone={mine.overdue > 0 ? 'warn' : 'good'}
        />
        <StatTile
          label={t('dashboard.slaCompliance')}
          value={mine.sla_compliance_pct ? `${mine.sla_compliance_pct}%` : '—'}
          tone={num(mine.sla_compliance_pct) >= 90 ? 'good' : 'warn'}
        />
      </div>

      <section>
        <h2 className="mb-2 text-sm font-semibold text-slate-700">{t('dashboard.myActivity')}</h2>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatTile label={t('leads.counts.calls')} value={mine.calls} />
          <StatTile label={t('leads.counts.whatsapp')} value={mine.whatsapp} />
          <StatTile label={t('leads.counts.meetings')} value={mine.meetings} />
          <StatTile label={t('leads.counts.visits')} value={mine.visits} />
        </div>
      </section>

      {/* A commission-driven rep looks at one thing first. */}
      <section>
        <h2 className="mb-2 text-sm font-semibold text-slate-700">{t('dashboard.myTargets')}</h2>
        <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4">
          <div>
            <div className="mb-1 flex justify-between text-xs text-slate-600">
              <span>{t('dashboard.revenueVsTarget')}</span>
              <span dir="ltr">
                {formatMoney(mine.revenue)} / {formatMoney(mine.target_revenue)}
              </span>
            </div>
            <Bar
              value={num(mine.revenue)}
              max={num(mine.target_revenue)}
              tone={num(mine.revenue_vs_target) >= 100 ? 'good' : 'brand'}
            />
          </div>
          <div>
            <div className="mb-1 flex justify-between text-xs text-slate-600">
              <span>{t('dashboard.myCommission')}</span>
              <span dir="ltr">{formatMoney(mine.agent_commission)}</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// ------------------------------------------------------------ shared pieces

export function FunnelChart() {
  const { t } = useTranslation();
  const { data, isPending } = useFunnel();

  if (isPending) return <Spinner />;
  const rows = data ?? [];
  const top = rows[0]?.reached ?? 0;

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-4">
      {rows.map((row) => (
        <div key={row.stage}>
          <div className="mb-1 flex items-baseline justify-between gap-2 text-xs">
            <span className="text-slate-700">{t(`stages.${row.stage}` as 'stages.new')}</span>
            <span className="tabular-nums text-slate-500" dir="ltr">
              {row.reached}
              {row.lost_here > 0 ? (
                <span className="ms-2 text-red-600">
                  −{row.lost_here} {t('dashboard.lostHere')}
                </span>
              ) : null}
            </span>
          </div>
          <Bar value={row.reached} max={top} tone={row.lost_here > 0 ? 'warn' : 'brand'} />
        </div>
      ))}
    </div>
  );
}

export function TeamTable() {
  const { t } = useTranslation();
  const { data, isPending } = useTeamScorecard();
  if (isPending) return <Spinner />;

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <table className="w-full text-sm">
        <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
          <tr>
            {(['team', 'leads', 'sales', 'revenue', 'closeRate', 'vsTarget'] as const).map((k) => (
              <th key={k} scope="col" className="px-3 py-2 text-start">
                {t(`dashboard.cols.${k}` as 'dashboard.cols.team')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {(data ?? []).map((row) => (
            <tr key={row.team_id} className="hover:bg-slate-50">
              <td className="px-3 py-2 font-medium text-slate-900">{row.team_name}</td>
              <td className="px-3 py-2 tabular-nums text-slate-700">{row.leads}</td>
              <td className="px-3 py-2 tabular-nums text-slate-700">{row.sales}</td>
              <td className="whitespace-nowrap px-3 py-2 text-slate-700" dir="ltr">
                {formatMoney(row.revenue)}
              </td>
              <td className="px-3 py-2 tabular-nums text-slate-700">
                {row.close_rate ? `${row.close_rate}%` : '—'}
              </td>
              <td className="px-3 py-2 tabular-nums text-slate-700">
                {row.revenue_vs_target ? `${row.revenue_vs_target}%` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AgentTable({ rows }: { rows: ReturnType<typeof useAgentScorecard>['data'] }) {
  const { t } = useTranslation();

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <table className="w-full text-sm">
        <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
          <tr>
            {(
              [
                'agent',
                'leads',
                'contacted',
                'qualified',
                'visited',
                'sales',
                'revenue',
                'sla',
              ] as const
            ).map((k) => (
              <th key={k} scope="col" className="px-3 py-2 text-start">
                {t(`dashboard.cols.${k}` as 'dashboard.cols.agent')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {(rows ?? []).map((row) => (
            <tr key={row.agent_id} className="hover:bg-slate-50">
              <td className="px-3 py-2">
                <div className="font-medium text-slate-900">{row.agent_name}</div>
                {row.never_contacted > 0 ? (
                  <div className="text-xs text-red-700">
                    {t('dashboard.neverContactedCount', { count: row.never_contacted })}
                  </div>
                ) : null}
              </td>
              <td className="px-3 py-2 tabular-nums text-slate-700">{row.leads}</td>
              <td className="px-3 py-2 tabular-nums text-slate-700">{row.contacted}</td>
              <td className="px-3 py-2 tabular-nums text-slate-700">{row.qualified}</td>
              <td className="px-3 py-2 tabular-nums text-slate-700">{row.visited}</td>
              <td className="px-3 py-2 tabular-nums text-slate-700">{row.sales}</td>
              <td className="whitespace-nowrap px-3 py-2 text-slate-700" dir="ltr">
                {formatMoney(row.revenue)}
              </td>
              <td className="px-3 py-2">
                <span
                  className={`tabular-nums ${
                    num(row.sla_compliance_pct) >= 90 ? 'text-green-700' : 'text-amber-700'
                  }`}
                >
                  {row.sla_compliance_pct ? `${row.sla_compliance_pct}%` : '—'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
